import { useModalStore } from "@stores/modals/modals.store";

const SPECKLE_CONNECT_STORAGE_KEY = "gaiup-speckle-connect";

export interface SpeckleConnectState {
  challenge: string;
  returnTo: string;
}

// Kicks off Speckle's authorize flow: the browser leaves for the Speckle consent screen and comes
// back on /integrations/speckle/callback with an access code. The challenge must be identical in
// the authorize URL and the later token exchange, so it's stashed for the callback page to consume.
// Returns false without navigating when the Speckle env vars aren't configured.
export const startSpeckleConnect = (returnTo: string): boolean => {
  const appId = import.meta.env.VITE_SPECKLE_APP_ID;
  const serverUrl = import.meta.env.VITE_SPECKLE_URL;
  if (!appId || !serverUrl) return false;

  const state: SpeckleConnectState = { challenge: crypto.randomUUID(), returnTo };
  sessionStorage.setItem(SPECKLE_CONNECT_STORAGE_KEY, JSON.stringify(state));
  window.location.assign(`${serverUrl}/authn/verify/${appId}/${state.challenge}`);
  return true;
};

// One-shot read: the state is removed immediately so a stale challenge can't be replayed later.
export const consumeSpeckleConnectState = (): SpeckleConnectState | null => {
  const raw = sessionStorage.getItem(SPECKLE_CONNECT_STORAGE_KEY);
  sessionStorage.removeItem(SPECKLE_CONNECT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const state: unknown = JSON.parse(raw);
    if (
      typeof state === "object" &&
      state !== null &&
      typeof (state as SpeckleConnectState).challenge === "string" &&
      typeof (state as SpeckleConnectState).returnTo === "string"
    ) {
      return state as SpeckleConnectState;
    }
    return null;
  } catch {
    // corrupted state — treat it as if the flow was never started
    return null;
  }
};

// Single place that knows how the Speckle settings modal is sized — used by the profile menu
// and by the connect callback page to reopen the modal after the flow completes.
export const openSpeckleModal = () => {
  return useModalStore().open("SpeckleModal", undefined, { wrapperClass: "max-w-lg min-w-0 min-h-[0px]" });
};
